import { useEffect, useState } from 'react';

import { Button } from '@gearbelt/ui/components';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { Accept, FileRejection } from 'react-dropzone';

import CheckCircleIcon from '@/components/icons/check-circle';
import UploadIcon from '@/components/icons/upload';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Dropzone, DropzoneContent, DropzoneDescription, DropzoneInput, DropzoneTitle } from '@/components/ui/dropzone';
import { cn } from '@/lib/utils';
import { readableFileSize } from '@/utils/file-size';

type UploadStatus = 'idle' | 'uploading' | 'complete' | 'error';

export interface AddDocumentUploadResult {
  file: File;
  url: string;
  mimeType: string;
  name: string;
}

interface AddDocumentUploadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (result: AddDocumentUploadResult) => void;
  uploadFile: (file: File) => Promise<{ url: string }>;
  acceptedFileTypes: Accept;
  acceptedFileTypesLabel: string;
  maxFileSizeBytes?: number;
}

export default function AddDocumentUploadModal({
  open,
  onOpenChange,
  onConfirm,
  uploadFile,
  acceptedFileTypes,
  acceptedFileTypesLabel,
  maxFileSizeBytes,
}: AddDocumentUploadModalProps) {
  const [status, setStatus] = useState<UploadStatus>('idle');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadedUrl, setUploadedUrl] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  function resetState() {
    setStatus('idle');
    setSelectedFile(null);
    setUploadedUrl(null);
    setErrorMessage(null);
  }

  useEffect(() => {
    if (!open) resetState();
  }, [open]);

  async function handleDropAccepted(files: File[]) {
    const file = files[0];
    if (!file) return;

    setSelectedFile(file);
    setStatus('uploading');
    setErrorMessage(null);

    try {
      const { url } = await uploadFile(file);
      setUploadedUrl(url);
      setStatus('complete');
    } catch (error) {
      setUploadedUrl(null);
      setStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Upload failed. Please try again.');
    }
  }

  function handleDropRejected(rejections: FileRejection[]) {
    const rejection = rejections[0];
    const reasons = rejection?.errors ?? [];

    let message: string;
    if (reasons.some((e) => e.code === 'file-invalid-type')) {
      message = 'File type is not supported';
    } else if (reasons.some((e) => e.code === 'file-too-large') && maxFileSizeBytes !== undefined) {
      message = `File exceeds the ${readableFileSize(maxFileSizeBytes)} limit`;
    } else if (reasons.some((e) => e.code === 'too-many-files')) {
      message = 'Only one file can be uploaded per document';
    } else {
      message = reasons[0]?.message ?? 'Could not read the file. Please try a different one';
    }

    setSelectedFile(null);
    setUploadedUrl(null);
    setStatus('error');
    setErrorMessage(message);
  }

  function handleDone() {
    if (status !== 'complete' || !selectedFile || !uploadedUrl) return;
    onConfirm({
      file: selectedFile,
      url: uploadedUrl,
      mimeType: selectedFile.type,
      name: selectedFile.name,
    });
    onOpenChange(false);
  }

  const isDone = status === 'complete';
  const isUploading = status === 'uploading';
  const hasError = status === 'error' && !!errorMessage;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex flex-col gap-4" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Upload a file from your computer</DialogTitle>
          <VisuallyHidden>
            <DialogDescription>Drag and drop a file or click the drop zone to upload a document.</DialogDescription>
          </VisuallyHidden>
        </DialogHeader>

        <Dropzone
          accept={acceptedFileTypes}
          maxSize={maxFileSizeBytes}
          maxFiles={1}
          multiple={false}
          onDropAccepted={handleDropAccepted}
          onDropRejected={handleDropRejected}
          disabled={isUploading || isDone}>
          <DropzoneContent
            className={cn('bg-grey-1 flex min-h-[200px] w-full flex-col items-center justify-center gap-2', isUploading && 'cursor-wait opacity-75')}>
            {status === 'idle' && (
              <>
                <DropzoneTitle>
                  <UploadIcon className="text-secondary-text size-8" />
                </DropzoneTitle>
                <DropzoneDescription>Drag and drop your file here or click to upload</DropzoneDescription>
                <DropzoneInput />
              </>
            )}

            {isUploading && selectedFile && (
              <div className="flex flex-col items-center gap-2">
                <div className="border-default-grey-2 border-t-default-blue size-8 animate-spin rounded-full border-4" />
                <div className="text-sm">Uploading {selectedFile.name}…</div>
                <DropzoneInput />
              </div>
            )}

            {isDone && selectedFile && (
              <div className="flex flex-col items-center gap-2">
                <CheckCircleIcon className="text-default-green size-8" />
                <div className="text-sm">{selectedFile.name}</div>
              </div>
            )}

            {hasError && (
              <>
                <DropzoneTitle>
                  <UploadIcon className="text-secondary-text size-8" />
                </DropzoneTitle>
                <DropzoneDescription>Drag and drop your file here or click to upload</DropzoneDescription>
                <DropzoneInput />
              </>
            )}
          </DropzoneContent>

          {hasError && <p className="text-default-red text-sm">{errorMessage}</p>}
        </Dropzone>

        <div className="text-secondary-text text-sm">
          <div>File types: {acceptedFileTypesLabel}</div>
          {maxFileSizeBytes !== undefined && <div>File size limit: {readableFileSize(maxFileSizeBytes)}</div>}
        </div>

        <Button variant="success" className="w-fit" type="button" onClick={handleDone} disabled={!isDone}>
          Done
        </Button>
      </DialogContent>
    </Dialog>
  );
}
